import { Suspense } from 'react';
import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import { metricWindowingFrontend } from '@gonfalon/dogfood-flags/dogfood-flags';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Sources } from '@gonfalon/metric-events/Sources';
import { toMetric, toObserveExpand } from '@gonfalon/navigator/navigators';
import type { GetMetricQueryParams } from '@gonfalon/openapi/operations/getMetric';
import type { DependentReleasePolicyRep } from '@gonfalon/openapi/types';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { CopyToClipboard } from '@gonfalon/ui/CopyToClipboard';
import { TextTruncator } from '@gonfalon/ui/TextTruncator';
import { TextTruncatorWithTooltip } from '@gonfalon/ui/TextTruncatorWithTooltip';
import { Button, LinkButton, ProgressBar, Tag, TagGroup, TagList } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { clsx } from 'clsx';

import { metricsSearchTracker, trackMetricsEvent } from '../../analytics';
import { DataSourceType } from '../../constants';
import { isImplicitWHNEventKey } from '../../isImplicitWHNEventKey';
import { isLDHosted } from '../../isLDHosted';
import { MetricDataSourceBadge } from '../../MetricDataSources';
import { MetricDefinition, metricDefinitionIncludesSuccessCriteria } from '../../MetricDefinition';
import { MetricEventDefault } from '../../MetricEventDefault';
import type { MetricKind } from '../../MetricKind';
import { MetricLabel } from '../../MetricLabel';
import { MetricLastSeenInfo } from '../../MetricLastSeenInfo';
import { MetricMeasurementWindow } from '../../MetricMeasurementWindow';
import { MetricSuccessCriteria } from '../../MetricSuccessCriteria';
import { MetricVersionTag } from '../../MetricVersionTag';
import { hasWinsorization, MetricWinsorization } from '../../MetricWinsorization';
import type { Metric } from '../../types';
import { useSuspenseMetric } from '../../useSuspenseMetric';

import { trackMetricSelectionLinkToFullResourceClicked } from './analytics';

import styles from './MetricDetailsPanel.module.css';

interface MetricDetailsPanelProps {
  metric: Pick<Metric, 'key'>;
  unavailableReason?: string;
  scopeByEnvironment?: boolean;
  externalLinkInTitle?: boolean;
}

// Component to render metric sources (similar to MetricSidebar)
function MetricSourcesSection({ metric, projectKey }: { metric: Metric; projectKey: string }) {
  const sources =
    metric.dataSource && !isLDHosted(metric.dataSource?.key) ? [metric.dataSource.key] : (metric.eventSources ?? []);

  if (metric.dataSource && metric.dataSource?.key && !isLDHosted(metric.dataSource.key)) {
    return (
      <MetricDataSourceBadge
        metricDataSource={{
          name: metric.dataSource?._name ?? '',
          integrationKey: metric.dataSource._integrationKey ?? '',
          key: metric.dataSource?.key ?? '',
          _projectSummary: { key: projectKey },
        }}
      />
    );
  }

  if (sources.length > 0) {
    return <Sources sources={sources.filter((src) => src !== undefined)} variant="small" />;
  }

  return (
    <span>
      <Message id="metrics.searchDetailsPanel.none" />
    </span>
  );
}

// Internal component that fetches full metric data
function MetricDetailsPanelInternal({
  metric,
  projectKey,
  unavailableReason,
  scopeByEnvironment,
  externalLinkInTitle = false,
}: {
  metric: Pick<Metric, 'key'>;
  projectKey: string;
  unavailableReason?: string;
  scopeByEnvironment?: boolean;
  externalLinkInTitle?: boolean;
}) {
  const selectedEnvironmentKey = useSelectedEnvironmentKey();
  const isMetricWindowingEnabled = metricWindowingFrontend();

  const metricQuery: GetMetricQueryParams = {
    expand: ['experiments', 'eventSources', 'guardedRolloutCount', 'releasePolicies', 'releasePolicyCount'],
  };
  const { data: fullMetric } = useSuspenseMetric(projectKey, metric.key, metricQuery, {
    staleTime: 5 * 60 * 1000,
  });

  const path = toMetric({ projectKey, metricKey: metric.key });
  const viewMetricLink = (
    <LinkButton
      href={path}
      target="_blank"
      className={styles.linkButton}
      aria-label={msg('metrics.searchDetailsPanel.viewMetricLabel')}
      onPress={() => {
        const attributes = { kind: 'metric' } as const;
        trackMetricSelectionLinkToFullResourceClicked(attributes);
        metricsSearchTracker.track('Link to Full Resource Clicked', attributes);
      }}
    >
      <Icon name="link-external" size="tiny" />
    </LinkButton>
  );

  // Grouped under the event key when there is one, otherwise shown under the definition
  // so trace metrics (filters, no event key) don't lose the indicator.
  const hasDisplayableEventKey =
    !!fullMetric.eventKey &&
    !isImplicitWHNEventKey({ eventKey: fullMetric.eventKey, dataSourceKey: fullMetric.dataSource?.key });
  const filtersIndicator = !!fullMetric.filters && (
    <span className={styles.filtersApplied}>
      <Icon name="filter-list" size="tiny" aria-hidden="true" />
      <Message id="metrics.searchDetailsPanel.eventFiltersApplied" />
    </span>
  );

  return (
    <div className={styles.panelContainer}>
      <div className={styles.detailsPanel}>
        {/* Last seen with environment */}
        <div className={styles.section}>
          <div className={styles.titleRow}>
            <TextTruncatorWithTooltip tooltipContent={fullMetric.name} className={styles.metricName} maxLines={2}>
              {fullMetric.name}
            </TextTruncatorWithTooltip>
            {externalLinkInTitle ? viewMetricLink : null}
          </div>
          <MetricLastSeenInfo
            metric={fullMetric}
            fallback={msg('metrics.searchDetailsPanel.notSeenRecently')}
            showEnvironment
            scopedToEnvironment={scopeByEnvironment ? selectedEnvironmentKey : undefined}
          />
          <div className={styles.metaRow}>
            <MetricLabel
              kind={fullMetric.kind as MetricKind}
              isNumeric={fullMetric.isNumeric}
              unitAggregationType={fullMetric.unitAggregationType}
            />
            <MetricVersionTag withSeparator version={fullMetric._version} />
          </div>
        </div>

        {unavailableReason && (
          <div className={styles.unavailableReason}>
            <div className={styles.unavailableReasonTitle}>
              <Icon size="tiny" name="info-circle-outline" className={styles.unavailableReasonIcon} />{' '}
              <Message id="metrics.searchDetailsPanel.unavailableForSelection" />
            </div>
            {unavailableReason}
          </div>
        )}

        {/* Metric structure */}
        <div className={styles.section}>
          <h4 className={styles.sectionHeader}>
            <Message id="metrics.searchDetailsPanel.metricStructure" />
          </h4>
          <div className={styles.sectionContent}>
            <TagGroup aria-label={msg('metrics.searchDetailsPanel.metricStructure')}>
              <TagList>
                <Tag size="small">
                  {fullMetric.denominator
                    ? msg('metrics.searchDetailsPanel.ratioStructure')
                    : msg('metrics.searchDetailsPanel.simpleStructure')}
                </Tag>
              </TagList>
            </TagGroup>
          </div>
        </div>

        {/* Description */}
        {fullMetric.description && (
          <div className={styles.section}>
            <h4 className={styles.sectionHeader}>
              <Message id="metrics.searchDetailsPanel.description" />
            </h4>
            <p className={styles.sectionContent}>{fullMetric.description}</p>
          </div>
        )}

        {/* Definition */}
        <div className={styles.section}>
          <h4 className={styles.sectionHeader}>
            <Message id="metrics.searchDetailsPanel.definition" />
          </h4>
          <div className={styles.sectionContent}>
            <MetricDefinition className={styles.metricDefinition} metric={fullMetric} />
            {!hasDisplayableEventKey && filtersIndicator}
          </div>
        </div>

        {/* Event key */}
        {hasDisplayableEventKey && (
          <div className={styles.section}>
            <h4 className={styles.sectionHeader}>
              <Message id="metrics.searchDetailsPanel.eventKey" />
            </h4>
            <div className={clsx(styles.sectionContent, styles.eventKeyContent)}>
              <CopyToClipboard
                text={fullMetric.eventKey ?? ''}
                tooltip={msg('metrics.searchDetailsPanel.copyEventKey')}
              >
                <Button
                  size="small"
                  data-test-id="event-key"
                  aria-label={msg('metrics.searchDetailsPanel.copyEventKeyLabel', {
                    eventKey: fullMetric.eventKey ?? '',
                  })}
                  className={styles.eventKey}
                >
                  <TextTruncator>{fullMetric.eventKey}</TextTruncator>
                  <Icon name="copy-clipboard" size="small" slot="end-icon" />
                </Button>
              </CopyToClipboard>
              {fullMetric.eventKey && (
                <LinkButton
                  size="small"
                  href={toObserveExpand({
                    projectKey,
                    environmentKey: selectedEnvironmentKey,
                    eventKey: fullMetric.eventKey,
                    eventKind: fullMetric.kind,
                    dataSourceKey: fullMetric.dataSource?.key ?? DataSourceType.LAUNCHDARKLY_HOSTED,
                  })}
                  target="_blank"
                  aria-label={msg('metrics.searchDetailsPanel.exploreEvent')}
                  onPress={() => trackMetricsEvent('Metric Sidebar Explore Event Clicked')}
                >
                  <Icon name="sparkle" size="small" slot="start-icon" />{' '}
                  {msg('metrics.searchDetailsPanel.exploreEvent')}
                </LinkButton>
              )}
              {filtersIndicator}
            </div>
          </div>
        )}

        {/* Units without events */}
        {!!fullMetric.eventDefault && (
          <div className={styles.section}>
            <h4 className={styles.sectionHeader}>
              <Message id="metrics.searchDetailsPanel.unitsWithout" values={{ kind: fullMetric.kind ?? 'custom' }} />
            </h4>
            <div className={styles.sectionContent}>
              <MetricEventDefault metric={fullMetric} />
            </div>
          </div>
        )}

        {/* Success criteria */}
        {(!!fullMetric.successCriteria || fullMetric.kind === 'click' || fullMetric.kind === 'pageview') &&
          !metricDefinitionIncludesSuccessCriteria(fullMetric) && (
            <div className={styles.section}>
              <h4 className={styles.sectionHeader}>
                <Message id="metrics.searchDetailsPanel.successCriteria" />
              </h4>
              <div className={styles.sectionContent}>
                <MetricSuccessCriteria metric={fullMetric} />
              </div>
            </div>
          )}

        {/* Measurement window */}
        {/* MetricMeasurementWindow returns null when windowStartOffset is non-zero but windowEndOffset is absent */}
        {isMetricWindowingEnabled &&
          !(
            fullMetric.windowStartOffset !== undefined &&
            fullMetric.windowStartOffset !== 0 &&
            fullMetric.windowEndOffset === undefined
          ) && (
            <div className={styles.section}>
              <h4 className={styles.sectionHeader}>
                <Message id="metrics.searchDetailsPanel.measurementWindow" />
              </h4>
              <div className={styles.sectionContent}>
                <MetricMeasurementWindow
                  windowStartOffset={fullMetric.windowStartOffset}
                  windowEndOffset={fullMetric.windowEndOffset}
                />
              </div>
            </div>
          )}

        {/* Winsorization  */}
        {hasWinsorization(fullMetric) && (
          <div className={styles.section}>
            <h4 className={styles.sectionHeader}>
              <Message id="metrics.searchDetailsPanel.winsorization" />
            </h4>
            <div className={styles.sectionContent}>
              <MetricWinsorization metric={fullMetric} />
            </div>
          </div>
        )}

        {/* Sources */}
        <div className={styles.section}>
          <h4 className={styles.sectionHeader}>
            <Message id="metrics.searchDetailsPanel.source" />
          </h4>
          <div className={styles.sectionContent}>
            <MetricSourcesSection metric={fullMetric} projectKey={projectKey} />
          </div>
        </div>

        {/* Release policies */}
        {fullMetric.releasePolicies && fullMetric.releasePolicies.length > 0 && (
          <div className={styles.section}>
            <h4 className={styles.sectionHeader}>
              <Message id="metrics.searchDetailsPanel.releasePolicies" />
            </h4>
            <div className={clsx(styles.sectionContent, styles.releasePolicyContent)}>
              {fullMetric.releasePolicies.map((policy: DependentReleasePolicyRep) => (
                <span key={policy.key} className={styles.releasePolicyItem}>
                  <TextTruncatorWithTooltip tooltipContent={policy.name}>{policy.name}</TextTruncatorWithTooltip>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      {externalLinkInTitle ? null : <div className={styles.linkButtonWrapper}>{viewMetricLink}</div>}
    </div>
  );
}

export function MetricDetailsPanel({
  metric,
  unavailableReason,
  scopeByEnvironment,
  externalLinkInTitle,
}: MetricDetailsPanelProps) {
  const projectKey = useProjectKey();

  return (
    <ErrorBoundary
      severity="medium"
      fallbackRender={() => (
        <div className={styles.errorContainer}>
          <span className={styles.errorMessage}>
            <Icon name="warning" className={styles.errorIcon} />
            <Message id="metrics.searchDetailsPanel.loadError" />
          </span>
        </div>
      )}
    >
      <Suspense
        fallback={
          <div className={styles.loadingContainer}>
            <ProgressBar size="small" isIndeterminate aria-label={msg('metrics.searchDetailsPanel.loadingLabel')} />
          </div>
        }
      >
        <MetricDetailsPanelInternal
          metric={metric}
          projectKey={projectKey}
          unavailableReason={unavailableReason}
          scopeByEnvironment={scopeByEnvironment}
          externalLinkInTitle={externalLinkInTitle}
        />
      </Suspense>
    </ErrorBoundary>
  );
}
